
import React, { useEffect, useState } from 'react';
import { fetchUsermedia, getUserInstagramMedia } from '../services/influancerService';
import { FaHeart, FaComment, FaEye, FaShare, FaUsers, FaArrowLeft, FaCheckCircle, FaClock, FaTimesCircle, FaFilter, FaTimes, FaList, FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { INFLUENCER_BASE } from '../constants';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const statusIcons = {
  pending: FaClock,
  approved: FaCheckCircle,
  rejected: FaTimesCircle,
};

const VIDEO_URL_RE = /\.(mp4|webm|ogg)(\?|$)/i;
const PLACEHOLDER_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
    <rect width="400" height="300" fill="#f3f4f6"/>
    <g fill="#9ca3af">
      <circle cx="170" cy="120" r="16"/>
      <path d="M120 190l42-48 31 36 22-25 55 63H120z"/>
    </g>
    <text x="200" y="230" text-anchor="middle" font-family="Arial, sans-serif" font-size="14" fill="#6b7280">
      Media preview unavailable
    </text>
  </svg>`
)}`;

const getSafePreview = (thumbnailCandidate, mediaCandidate) => {
  const normalizedThumbnail = thumbnailCandidate || null;
  const normalizedMedia = mediaCandidate || null;
  const thumbnailIsVideo = VIDEO_URL_RE.test(String(normalizedThumbnail || ''));
  const mediaIsVideo = VIDEO_URL_RE.test(String(normalizedMedia || ''));

  const safeThumbnail = thumbnailIsVideo ? null : normalizedThumbnail;
  const previewSource = safeThumbnail || normalizedMedia || PLACEHOLDER_IMAGE;
  const shouldRenderVideo = !safeThumbnail && mediaIsVideo;

  return { previewSource, shouldRenderVideo };
};

const FilterChip = ({ label, onRemove }) => (
  <div className="mb-2 mr-2 inline-flex items-center rounded-full bg-gray-100 px-3 py-1">
    <span className="text-xs text-gray-700 sm:text-sm">{label}</span>
    <button type="button" onClick={onRemove} className="ml-2 text-gray-500 hover:text-gray-700">
      <FaTimes size={12} />
    </button>
  </div>
);

const CampaignPopup = ({ campaigns, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-h-96 w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">Campaigns</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600">
            <FaTimes size={16} />
          </button>
        </div>

        <div className="space-y-2">
          {campaigns.map((campaign, index) => (
            <div key={index} className="flex items-center justify-between rounded-xl bg-gray-50 p-3">
              <div className="min-w-0 flex-1">
                <h4 className="truncate text-sm font-medium text-gray-900">{campaign.campaignName}</h4>
                <p className="text-xs text-gray-500">Status: {campaign.status}</p>
              </div>
              <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-xs ${statusColors[campaign.status]}`}>
                {campaign.status}
              </span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          Close
        </button>
      </div>
    </div>
  );
};

const FilterSection = ({ title, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-gray-100">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-3 text-sm font-semibold text-gray-800"
      >
        {title}
        <FaChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="space-y-2 pb-3">{children}</div>}
    </div>
  );
};

const InstaMediaDashboard = () => {
  const navigate = useNavigate();
  const [mediaData, setMediaData] = useState([]);
  const [instagramMedia, setInstagramMedia] = useState([]);
  const [filteredCampaignMedia, setFilteredCampaignMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('campaigns'); // 'instagram' or 'campaigns' - default to campaigns
  const [statusFilter, setStatusFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showCampaignPopup, setShowCampaignPopup] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const getMedia = async () => {
      setLoading(true);
      try {
        // Fetch both Instagram API media and campaign media
        const [apiResponse, campaignResponse] = await Promise.allSettled([
          fetchUsermedia(),
          getUserInstagramMedia()
        ]);

        if (apiResponse.status === 'fulfilled') {
          setMediaData(apiResponse.value.data.media || []);
        }

        if (campaignResponse.status === 'fulfilled') {
          const sortedMedia = (campaignResponse.value.posts || []).sort((a, b) => {
            const dateA = new Date(a.publishedAt || 0);
            const dateB = new Date(b.publishedAt || 0);
            return dateB - dateA;
          });
          setInstagramMedia(sortedMedia);
          setFilteredCampaignMedia(sortedMedia);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching media:', err);
        setError('Failed to fetch Instagram media. Please try again later.');
        setLoading(false);
      }
    };
    getMedia();
  }, []);

  // Apply filters whenever instagramMedia, statusFilter, or campaignFilter changes
  useEffect(() => {
    let filtered = [...instagramMedia];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(post => {
        const overallStatus = getOverallStatus(post);
        return overallStatus.status === statusFilter;
      });
    }

    if (campaignFilter !== 'all') {
      filtered = filtered.filter(post => {
        if (!post.campaignApprovals || post.campaignApprovals.length === 0) {
          return false;
        }
        return post.campaignApprovals.some(approval =>
          approval.campaignName === campaignFilter
        );
      });
    }

    setFilteredCampaignMedia(filtered);
  }, [instagramMedia, statusFilter, campaignFilter]);

  const getOverallStatus = (post) => {
    if (!post.campaignApprovals || post.campaignApprovals.length === 0) {
      return { status: 'pending', text: 'No campaigns' };
    }

    const allApproved = post.campaignApprovals.every(approval => approval.status === 'approved');
    const anyRejected = post.campaignApprovals.some(approval => approval.status === 'rejected');
    const anyPending = post.campaignApprovals.some(approval => approval.status === 'pending');

    if (allApproved) {
      return { status: 'approved', text: 'All campaigns approved' };
    } else if (anyRejected) {
      return { status: 'rejected', text: 'Some campaigns rejected' };
    } else if (anyPending) {
      return { status: 'pending', text: 'Pending approval' };
    }

    return { status: 'pending', text: 'No campaigns' };
  };

  const handleFilterChange = (newStatus) => {
    setStatusFilter(newStatus);

    // Update active filters
    const newFilters = [...activeFilters];
    const existingFilterIndex = newFilters.findIndex(f => f.type === 'status');

    if (existingFilterIndex >= 0) {
      if (newStatus === 'all') {
        newFilters.splice(existingFilterIndex, 1);
      } else {
        newFilters[existingFilterIndex].value = newStatus;
      }
    } else if (newStatus !== 'all') {
      newFilters.push({ type: 'status', value: newStatus });
    }

    setActiveFilters(newFilters);
  };

  const handleCampaignFilterChange = (newCampaign) => {
    setCampaignFilter(newCampaign);

    // Update active filters
    const newFilters = [...activeFilters];
    const existingFilterIndex = newFilters.findIndex(f => f.type === 'campaign');

    if (existingFilterIndex >= 0) {
      if (newCampaign === 'all') {
        newFilters.splice(existingFilterIndex, 1);
      } else {
        newFilters[existingFilterIndex].value = newCampaign;
      }
    } else if (newCampaign !== 'all') {
      newFilters.push({ type: 'campaign', value: newCampaign });
    }

    setActiveFilters(newFilters);
  };

  const resetFilters = () => {
    setStatusFilter('all');
    setCampaignFilter('all');
    setActiveFilters([]);
  };

  const getCampaignList = () => {
    const campaigns = new Set();
    instagramMedia.forEach(post => {
      if (post.campaignApprovals) {
        post.campaignApprovals.forEach(approval => {
          if (approval.campaignName) {
            campaigns.add(approval.campaignName);
          }
        });
      }
    });
    return Array.from(campaigns).sort();
  };

  const openCampaignPopup = (post) => {
    setSelectedPost(post);
    setShowCampaignPopup(true);
  };

  const closeCampaignPopup = () => {
    setShowCampaignPopup(false);
    setSelectedPost(null);
  };

  if (loading) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner message="Fetching your Instagram Media..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] w-full flex-col items-center justify-center p-4 lg:p-6">
        <p className="text-center text-sm font-semibold text-red-600 sm:text-base">{error}</p>
      </div>
    );
  }

  const handleBackClick = () => {
    navigate(`${INFLUENCER_BASE}/dashboard`);
  };

  const campaignList = getCampaignList();

  return (
    <div className="w-full p-4 lg:p-6">
      <button
        type="button"
        onClick={handleBackClick}
        className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600 lg:mb-4"
      >
        <FaArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>

      <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">Instagram Media</h1>
            <p className="mt-1 text-sm text-gray-500">Track campaign posts and Instagram media performance.</p>
          </div>

          {activeTab === 'campaigns' && (
            <div className="flex shrink-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:text-sm"
              >
                <FaFilter className="h-3.5 w-3.5" />
                Filters
                {activeFilters.length > 0 && (
                  <span className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    {activeFilters.length}
                  </span>
                )}
              </button>
              <span className="text-xs text-gray-500 sm:text-sm">
                Showing {filteredCampaignMedia.length} of {instagramMedia.length}
              </span>
            </div>
          )}
        </div>

        {activeTab === 'campaigns' && activeFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap border-t border-gray-100 pt-3">
            {activeFilters.map((filter, index) => (
              <FilterChip
                key={index}
                label={`${filter.type}: ${filter.value}`}
                onRemove={() => {
                  if (filter.type === 'status') handleFilterChange('all');
                  else if (filter.type === 'campaign') handleCampaignFilterChange('all');
                }}
              />
            ))}
          </div>
        )}

        <div className="mt-4 flex gap-1 overflow-x-auto border-b border-gray-200">
          <button
            type="button"
            className={`shrink-0 border-b-2 px-3 py-2 text-xs font-medium transition sm:px-4 sm:text-sm ${
              activeTab === 'campaigns'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('campaigns')}
          >
            Campaign Media ({instagramMedia.length})
          </button>
          <button
            type="button"
            className={`shrink-0 border-b-2 px-3 py-2 text-xs font-medium transition sm:px-4 sm:text-sm ${
              activeTab === 'instagram'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('instagram')}
          >
            All Instagram Posts ({mediaData.length})
          </button>
        </div>
      </div>

      {activeTab === 'instagram' && (
        mediaData.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
            <p className="text-sm text-gray-500">No Instagram posts found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
            {mediaData.map((post) => {
              const { previewSource, shouldRenderVideo } = getSafePreview(post.thumbnailUrl, post.mediaUrl);

              return (
                <div key={post.postId} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md">
                  {shouldRenderVideo ? (
                    <video
                      src={previewSource}
                      className="h-44 w-full object-cover sm:h-48"
                      muted
                      playsInline
                      preload="metadata"
                      onError={(e) => {
                        e.currentTarget.poster = PLACEHOLDER_IMAGE;
                        e.currentTarget.removeAttribute('src');
                        e.currentTarget.load();
                      }}
                    />
                  ) : (
                    <img
                      src={previewSource}
                      alt={post.caption}
                      className="h-44 w-full object-cover sm:h-48"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = PLACEHOLDER_IMAGE;
                      }}
                    />
                  )}
                  <div className="p-4">
                    <h2 className="mb-2 truncate text-sm font-semibold text-gray-900">{post.caption || 'No Caption'}</h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                      <span className="inline-flex items-center gap-1"><FaHeart className="text-red-500" />{post.insights?.likes || 0}</span>
                      <span className="inline-flex items-center gap-1"><FaComment className="text-blue-500" />{post.insights?.comments || 0}</span>
                      {post.mediaType === 'VIDEO' && (
                        <span className="inline-flex items-center gap-1"><FaEye className="text-green-500" />{post.insights?.views || 0}</span>
                      )}
                      <span className="inline-flex items-center gap-1"><FaUsers className="text-purple-500" />{post.insights?.reach || 0}</span>
                      <span className="inline-flex items-center gap-1"><FaShare className="text-yellow-500" />{post.insights?.shares || 0}</span>
                      <span><b>Total:</b> {post.insights?.total_interactions || 0}</span>
                    </div>
                    <p className="mt-2 text-xs text-gray-400">{new Date(post.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {activeTab === 'campaigns' && (
        <>
          {filteredCampaignMedia.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
              <FaFilter className="mb-3 h-10 w-10 text-gray-300" />
              <h3 className="text-base font-semibold text-gray-900">No media found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter === 'all' && campaignFilter === 'all'
                  ? 'No Instagram campaign media found. Add some media to campaigns to get started!'
                  : 'No media found with current filters. Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
              {filteredCampaignMedia.map((post) => {
                const overallStatus = getOverallStatus(post);
                const StatusIcon = statusIcons[overallStatus.status];
                const campaignCount = post.campaignApprovals?.length || 0;
                const { previewSource, shouldRenderVideo } = getSafePreview(post.thumbnailUrl, post.mediaUrls?.[0]);

                return (
                  <div key={post.postId} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md">
                    <div className="relative">
                      {shouldRenderVideo ? (
                        <video
                          src={previewSource}
                          className="h-44 w-full object-cover sm:h-48"
                          muted
                          playsInline
                          preload="metadata"
                          onError={(e) => {
                            e.currentTarget.poster = PLACEHOLDER_IMAGE;
                            e.currentTarget.removeAttribute('src');
                            e.currentTarget.load();
                          }}
                        />
                      ) : (
                        <img
                          src={previewSource}
                          alt={post.caption}
                          className="h-44 w-full object-cover sm:h-48"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = PLACEHOLDER_IMAGE;
                          }}
                        />
                      )}
                      <div className="absolute right-2 top-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold sm:text-xs ${statusColors[overallStatus.status]}`}>
                          <StatusIcon className="h-3 w-3" />
                          {overallStatus.status}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h2 className="mb-2 truncate text-sm font-semibold text-gray-900">{post.caption || 'No Caption'}</h2>

                      {campaignCount > 0 && (
                        <button
                          type="button"
                          onClick={() => openCampaignPopup(post)}
                          className="mb-3 flex w-full items-center justify-between rounded-xl bg-gray-50 p-2 text-xs transition hover:bg-gray-100 sm:text-sm"
                        >
                          <span className="inline-flex items-center gap-2 text-gray-700">
                            <FaList className="text-gray-400" />
                            {campaignCount} Campaign{campaignCount !== 1 ? 's' : ''}
                          </span>
                          <FaChevronDown className="text-gray-400" />
                        </button>
                      )}

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                        <span className="inline-flex items-center gap-1"><FaHeart className="text-red-500" />{post.statistics?.likeCount || '0'}</span>
                        <span className="inline-flex items-center gap-1"><FaComment className="text-blue-500" />{post.statistics?.commentCount || '0'}</span>
                        {(post.mediaType === 'VIDEO' || post.mediaType === 'REEL') && (
                          <span className="inline-flex items-center gap-1"><FaEye className="text-green-500" />{post.statistics?.viewCount || '0'}</span>
                        )}
                        <span className="inline-flex items-center gap-1"><FaUsers className="text-purple-500" />{post.statistics?.reach || '0'}</span>
                        <span className="inline-flex items-center gap-1"><FaShare className="text-yellow-500" />{post.statistics?.shares || '0'}</span>
                      </div>

                      <p className="mt-2 text-xs text-gray-400">
                        {post.publishedAt ? new Date(post.publishedAt).toLocaleString() : 'Unknown date'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {filterOpen && (
        <div className="fixed inset-x-0 bottom-0 top-20 z-[130]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFilterOpen(false)} />
          <div className="absolute bottom-0 right-0 top-0 flex w-full flex-col bg-white shadow-xl sm:w-[380px]">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h3 className="text-sm font-bold text-gray-900 sm:text-base">Filters</h3>
              <div className="flex items-center gap-3">
                <button type="button" onClick={resetFilters} className="text-xs text-gray-500 transition hover:text-gray-700 sm:text-sm">
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setFilterOpen(false)}
                  className="rounded-xl bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-orange-600 sm:text-sm"
                >
                  Done
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4">
              <FilterSection title="Status">
                {['pending', 'approved', 'rejected'].map((status) => (
                  <label key={status} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="radio" checked={statusFilter === status} onChange={() => handleFilterChange(status)} />
                    <span className="capitalize">{status}</span>
                  </label>
                ))}
              </FilterSection>
              <FilterSection title="Campaign">
                {campaignList.map((campaign) => (
                  <label key={campaign} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="radio" checked={campaignFilter === campaign} onChange={() => handleCampaignFilterChange(campaign)} />
                    {campaign}
                  </label>
                ))}
              </FilterSection>
            </div>
          </div>
        </div>
      )}

      <CampaignPopup
        campaigns={selectedPost?.campaignApprovals || []}
        isOpen={showCampaignPopup}
        onClose={closeCampaignPopup}
      />
    </div>
  );
};

export default InstaMediaDashboard;
